import { useEffect, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { api, apiError, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { btnPrimary, cardCls, inputCls, labelCls } from "./shared";

export default function SiteTab({ settings, reload }) {
  const [form, setForm] = useState(null);
  useEffect(() => { if (settings) setForm({ ...settings }); }, [settings]);
  const logoRef = useRef(null);
  const coverRef = useRef(null);

  if (!form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    const body = { announcement_text: form.announcement_text, whatsapp: form.whatsapp, instagram: form.instagram, email: form.email, owner_email: form.owner_email, address: form.address, upi_id: form.upi_id, bank_account_name: form.bank_account_name, bank_account_number: form.bank_account_number, bank_ifsc: form.bank_ifsc, bank_name: form.bank_name, cod_enabled: !!form.cod_enabled, upi_enabled: !!form.upi_enabled, bank_enabled: !!form.bank_enabled, footer_note: form.footer_note, store_name: form.store_name, about_text: form.about_text };
    try {
      await api.put("/admin/site-settings", body);
      toast.success("Site settings saved");
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  const uploadAsset = (kind, ref) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/site-assets", fd, { params: { kind } });
      toast.success(`${kind === "logo" ? "Logo" : "Cover"} updated`);
      reload();
    } catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="admin-site-tab">
      <div className={cardCls}>
        <p className="font-display text-lg">Brand assets</p>
        <div className="mt-4 flex flex-wrap gap-6">
          <div className="text-center">
            {form.logo_url && <img src={imgUrl(form.logo_url)} alt="Logo" className="h-24 w-24 rounded-full object-cover ring-1 ring-primary/30" data-testid="site-logo-preview" />}
            <button type="button" data-testid="logo-upload-btn" onClick={() => logoRef.current?.click()}
              className="mt-2 flex items-center gap-1 rounded-full border border-input px-4 py-2 text-xs uppercase tracking-widest hover:bg-secondary"><Upload size={12} /> Logo</button>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="logo-upload-input" onChange={uploadAsset("logo")} />
          </div>
          <div className="text-center">
            {form.cover_image_url && <img src={imgUrl(form.cover_image_url)} alt="Cover" className="h-24 w-36 rounded-2xl object-cover ring-1 ring-primary/30" data-testid="site-cover-preview" />}
            <button type="button" data-testid="cover-upload-btn" onClick={() => coverRef.current?.click()}
              className="mt-2 flex items-center gap-1 rounded-full border border-input px-4 py-2 text-xs uppercase tracking-widest hover:bg-secondary"><Upload size={12} /> Cover</button>
            <input ref={coverRef} type="file" accept="image/*" className="hidden" data-testid="cover-upload-input" onChange={uploadAsset("cover")} />
          </div>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className={labelCls}>Store name<input data-testid="site-store-name-input" className={inputCls} value={form.store_name || ""} onChange={set("store_name")} placeholder="Anaya Fashion & Gifts" /></label>
          <label className={labelCls}>Address<input data-testid="site-address-input" className={inputCls} value={form.address || ""} onChange={set("address")} /></label>
          <label className={`${labelCls} sm:col-span-2`}>About Us content (one paragraph per line — shown on the Our Story page)
            <textarea data-testid="site-about-input" rows={5} className={inputCls} value={form.about_text || ""} onChange={set("about_text")} placeholder="Leave empty to keep the current story" /></label>
        </div>
      </div>
      <div className={cardCls}>
        <p className="font-display text-lg">Content &amp; contact</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className={`${labelCls} sm:col-span-2`}>Announcement ticker<input data-testid="site-ticker-input" className={inputCls} value={form.announcement_text || ""} onChange={set("announcement_text")} /></label>
          <label className={labelCls}>WhatsApp (country code + number)<input data-testid="site-whatsapp-input" className={inputCls} value={form.whatsapp || ""} onChange={set("whatsapp")} /></label>
          <label className={labelCls}>Instagram URL<input data-testid="site-instagram-input" className={inputCls} value={form.instagram || ""} onChange={set("instagram")} /></label>
          <label className={labelCls}>Email<input data-testid="site-email-input" className={inputCls} value={form.email || ""} onChange={set("email")} /></label>
          <label className={labelCls}>Order alert email<input data-testid="site-owner-email-input" className={inputCls} value={form.owner_email || ""} onChange={set("owner_email")} /></label>
          <label className={`${labelCls} sm:col-span-2`}>Footer note<input data-testid="site-footer-input" className={inputCls} value={form.footer_note || ""} onChange={set("footer_note")} /></label>
        </div>
      </div>
      <div className={cardCls}>
        <p className="font-display text-lg">Payment information</p>
        <div className="mt-3 flex flex-wrap gap-5">
          {[["cod_enabled", "COD"], ["upi_enabled", "UPI"], ["bank_enabled", "Bank transfer"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-xs uppercase tracking-widest">
              <input type="checkbox" data-testid={`site-${k}`} checked={!!form[k]} onChange={set(k)} className="accent-primary" /> {l}
            </label>
          ))}
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className={labelCls}>UPI ID<input data-testid="site-upi-input" className={inputCls} value={form.upi_id || ""} onChange={set("upi_id")} /></label>
          <label className={labelCls}>Bank name<input data-testid="site-bank-name-input" className={inputCls} value={form.bank_name || ""} onChange={set("bank_name")} /></label>
          <label className={labelCls}>Account name<input data-testid="site-bank-holder-input" className={inputCls} value={form.bank_account_name || ""} onChange={set("bank_account_name")} /></label>
          <label className={labelCls}>Account number<input data-testid="site-bank-number-input" className={inputCls} value={form.bank_account_number || ""} onChange={set("bank_account_number")} /></label>
          <label className={labelCls}>IFSC<input data-testid="site-bank-ifsc-input" className={inputCls} value={form.bank_ifsc || ""} onChange={set("bank_ifsc")} /></label>
        </div>
      </div>
      <button type="submit" data-testid="site-save-btn" className={btnPrimary}>Save settings</button>
    </form>
  );
}
